import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, CheckCircle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LiveChatWidget() {
  const [open, setOpen]           = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading]     = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [todayCount, setTodayCount] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", message: "" });

  useEffect(() => {
    axios.get(`${API}/enquiry-stats`)
      .then(r => setTodayCount(r.data.today))
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.message) return;
    setLoading(true);
    setSubmitError("");
    try {
      await axios.post(`${API}/quick-chat`, form);
      setSubmitted(true);
    } catch {
      setSubmitError("Sorry — we couldn't send your message. Please try again or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setSubmitted(false);
    setSubmitError("");
    setForm({ name: "", phone: "", message: "" });
  };

  return (
    /* positioned just above the WhatsApp button */
    <div className="fixed bottom-[88px] right-6 z-50">
      <AnimatePresence>
        {open && (
          <motion.div
            key="panel"
            initial={{ opacity: 0, y: 16, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="w-80 bg-white shadow-2xl border border-gray-200 mb-3 overflow-hidden"
            data-testid="live-chat-panel"
          >
            {/* Header */}
            <div className="bg-[#0F62FE] px-4 py-3.5 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="relative">
                  <div className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center">
                    <MessageCircle size={17} className="text-white" />
                  </div>
                  <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-400 rounded-full border-2 border-[#0F62FE]" />
                </div>
                <div>
                  <p className="text-white font-bold text-sm leading-none">Jain Infotech</p>
                  <p className="text-blue-200 text-[11px] mt-0.5">Typically replies within 2 hrs</p>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                data-testid="live-chat-close-btn"
                className="text-white/60 hover:text-white transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="p-4">
              {submitted ? (
                <div className="text-center py-5" data-testid="live-chat-success">
                  <CheckCircle size={36} className="text-green-500 mx-auto mb-2" />
                  <p className="font-bold text-[#111827] text-sm mb-1">Message Received!</p>
                  <p className="text-xs text-[#4B5563]">We'll call you back shortly.</p>
                  <button onClick={reset} className="mt-3 text-xs text-[#0F62FE] underline">
                    Send another
                  </button>
                </div>
              ) : (
                <>
                  {todayCount > 0 && (
                    <div className="mb-3 px-3 py-2 bg-[#F3F4F6] border border-gray-200 text-xs text-[#4B5563] flex items-center gap-2">
                      <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse shrink-0" />
                      <span>
                        <strong className="text-[#111827]">{todayCount} {todayCount === 1 ? "company" : "companies"}</strong>
                        {" "}sent enquiries today
                      </span>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-3" data-testid="live-chat-form">
                    <input
                      value={form.name}
                      onChange={e => setForm({ ...form, name: e.target.value })}
                      placeholder="Your Name *"
                      required
                      data-testid="chat-name-input"
                      className="w-full px-3 py-2.5 border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE] focus:border-transparent"
                    />
                    <input
                      value={form.phone}
                      onChange={e => setForm({ ...form, phone: e.target.value })}
                      placeholder="Phone Number *"
                      type="tel"
                      required
                      data-testid="chat-phone-input"
                      className="w-full px-3 py-2.5 border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE] focus:border-transparent"
                    />
                    <textarea
                      value={form.message}
                      onChange={e => setForm({ ...form, message: e.target.value })}
                      placeholder="What are you looking for?"
                      rows={3}
                      required
                      data-testid="chat-message-input"
                      className="w-full px-3 py-2.5 border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE] focus:border-transparent resize-none"
                    />
                    {submitError && (
                      <p
                        data-testid="live-chat-error"
                        className="text-xs text-red-600 bg-red-50 border border-red-200 px-3 py-2 leading-snug"
                      >
                        {submitError}
                      </p>
                    )}
                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="chat-submit-btn"
                      className="w-full flex items-center justify-center gap-2 bg-[#0F62FE] hover:bg-[#0052CC] disabled:bg-gray-400 text-white text-sm font-semibold py-2.5 transition-colors"
                    >
                      {loading ? (
                        <span className="animate-spin border-2 border-white border-t-transparent rounded-full w-4 h-4" />
                      ) : (
                        <><Send size={14} /> Send Message</>
                      )}
                    </button>
                  </form>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Trigger button */}
      <button
        onClick={() => setOpen(!open)}
        data-testid="live-chat-toggle-btn"
        className="relative flex items-center gap-2 bg-[#0F62FE] hover:bg-[#0052CC] text-white font-semibold px-4 py-3 rounded-full shadow-xl transition-all duration-200 hover:shadow-2xl hover:scale-105"
      >
        {open ? <X size={20} /> : <MessageCircle size={20} />}
        {!open && <span className="text-sm">Live Chat</span>}
        {!open && (
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-white animate-pulse" />
        )}
      </button>
    </div>
  );
}
